/**
 * Created on Fri Jun 12 2026 4:08:23 PM
 *
 * *************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2026
 * All Rights Reserved
 *
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ************************************************************************************/


import { useCallback, useEffect, useMemo, useRef } from 'react';
import { resolveSectionLayout } from './layoutResolver';
import { matchElements } from './elementMatcher';
import { applyLayout } from './layoutApplier';

export const DEFAULT_REAPPLY_DELAY_MS = 150;

export const MAX_REAPPLY_DELAY_MS = 200;

const defaultScheduler = {
  schedule: (fn, delayMs) => setTimeout(fn, delayMs),
  cancel: (handle) => clearTimeout(handle),
};

export function readContainerWidth(el) {
  if (el) {
    if (typeof el.clientWidth === 'number' && el.clientWidth > 0) {
      return el.clientWidth;
    }
    if (typeof el.getBoundingClientRect === 'function') {
      const rect = el.getBoundingClientRect();
      if (rect && Number.isFinite(rect.width) && rect.width > 0) {
        return rect.width;
      }
    }
  }
  if (typeof window !== 'undefined' && Number.isFinite(window.innerWidth)) {
    return window.innerWidth;
  }
  return 0;
}

export function applyElementLayoutPass(sectionEl, section, { viewportWidth } = {}) {
  const resolved = resolveSectionLayout(section);
  if (!resolved) return null; // NO-OP: no valid layout data -> leave section untouched.
  if (!sectionEl) return null;

  const root = (typeof sectionEl.closest === 'function' && sectionEl.closest('[data-reader-section]')) || sectionEl;
  const matched = matchElements(root, resolved.entries);
  return applyLayout(matched, {
    viewportWidth,
    referenceWidth: resolved.referenceWidth,
  });
}

export function useElementLayout(sectionRef, section, options = {}) {
  const {
    scheduler = defaultScheduler,
    reapplyDelayMs = DEFAULT_REAPPLY_DELAY_MS,
    getViewportWidth = readContainerWidth,
    observeViewport = true,
  } = options;

  const delayMs = useMemo(() => {
    const raw = Number(reapplyDelayMs);
    if (!Number.isFinite(raw) || raw < 0) return DEFAULT_REAPPLY_DELAY_MS;
    return Math.min(raw, MAX_REAPPLY_DELAY_MS);
  }, [reapplyDelayMs]);

  const hasLayout = useMemo(() => resolveSectionLayout(section) !== null, [section]);

  const sectionDataRef = useRef(section);
  sectionDataRef.current = section;
  const getViewportWidthRef = useRef(getViewportWidth);
  getViewportWidthRef.current = getViewportWidth;

  const reapply = useCallback(() => {
    const sectionEl = sectionRef && sectionRef.current;
    if (!sectionEl) return null;
    const viewportWidth = getViewportWidthRef.current(sectionEl);
    return applyElementLayoutPass(sectionEl, sectionDataRef.current, { viewportWidth });
  }, [sectionRef]);

  useEffect(() => {
    if (!hasLayout) return undefined;
    reapply();
    return undefined;
  }, [hasLayout, reapply]);

  // Re-apply when the section's subtree changes (e.g. async content like an 800ms
  // loading state that swaps out the DOM after initial mount). Without this, a section
  // that renders a placeholder on mount and then replaces it with real content would
  // have no target nodes for the element-layout applier to patch.
  useEffect(() => {
    if (!hasLayout) return undefined;
    const sectionEl = sectionRef && sectionRef.current;
    if (!sectionEl || typeof MutationObserver === 'undefined') return undefined;

    let pendingHandle = null;
    const observer = new MutationObserver(() => {
      if (pendingHandle !== null) clearTimeout(pendingHandle);
      pendingHandle = setTimeout(() => {
        pendingHandle = null;
        reapply();
      }, 0);
    });
    observer.observe(sectionEl, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (pendingHandle !== null) clearTimeout(pendingHandle);
    };
  }, [hasLayout, reapply, sectionRef]);

  // Enterprise robustness: re-apply once asynchronous content settles. Absolute
  // placement measures an element's NATURAL offset within its section, which shifts when
  // web fonts swap (text metrics change) or images/content load late. The ResizeObserver
  // below catches changes to the section's own size, but a reflow that moves content
  // WITHOUT changing the section's measured size (e.g. a fixed-height section) would not
  // trip it — so we also re-apply on fonts.ready, full window load, and in-section image
  // loads. This guarantees the first painted frame matches the builder, not just frames
  // after a resize.
  useEffect(() => {
    if (!hasLayout) return undefined;
    if (typeof window === 'undefined') return undefined;

    let cancelled = false;
    const run = () => { if (!cancelled) reapply(); };

    if (typeof document !== 'undefined'
      && document.fonts
      && document.fonts.ready
      && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(run).catch(() => {});
    }

    let onLoad = null;
    if (typeof document === 'undefined' || document.readyState !== 'complete') {
      onLoad = run;
      window.addEventListener('load', onLoad);
    }

    // Re-apply when images inside this section finish loading (their height affects the
    // natural offset of elements below them in the same section).
    const sectionEl = sectionRef && sectionRef.current;
    const imageNodes = [];
    if (sectionEl && typeof sectionEl.querySelectorAll === 'function') {
      const imgs = sectionEl.querySelectorAll('img');
      for (let i = 0; i < imgs.length; i += 1) {
        const img = imgs[i];
        if (img && img.complete === false) {
          img.addEventListener('load', run);
          img.addEventListener('error', run);
          imageNodes.push(img);
        }
      }
    }

    return () => {
      cancelled = true;
      if (onLoad) window.removeEventListener('load', onLoad);
      for (let i = 0; i < imageNodes.length; i += 1) {
        imageNodes[i].removeEventListener('load', run);
        imageNodes[i].removeEventListener('error', run);
      }
    };
  }, [hasLayout, reapply, sectionRef]);

  useEffect(() => {
    if (!hasLayout || !observeViewport) return undefined;

    const sectionEl = sectionRef && sectionRef.current;
    if (!sectionEl || typeof window === 'undefined') return undefined;

    let pendingHandle = null;
    let lastWidth = getViewportWidthRef.current(sectionEl);

    const scheduleReapply = () => {
      if (pendingHandle !== null) scheduler.cancel(pendingHandle);
      pendingHandle = scheduler.schedule(() => {
        pendingHandle = null;
        reapply();
      }, delayMs);
    };

    const handleWidthChange = (nextWidth) => {
      if (Number.isFinite(nextWidth) && nextWidth === lastWidth) return;
      lastWidth = nextWidth;
      scheduleReapply();
    };

    let resizeObserver = null;
    let onWindowResize = null;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        handleWidthChange(getViewportWidthRef.current(sectionEl));
      });
      resizeObserver.observe(sectionEl);
    } else {
      onWindowResize = () => handleWidthChange(getViewportWidthRef.current(sectionEl));
      window.addEventListener('resize', onWindowResize);
    }

    return () => {
      if (pendingHandle !== null) {
        scheduler.cancel(pendingHandle);
        pendingHandle = null;
      }
      if (resizeObserver) resizeObserver.disconnect();
      if (onWindowResize) window.removeEventListener('resize', onWindowResize);
    };
  }, [hasLayout, observeViewport, sectionRef, scheduler, delayMs, reapply]);

  return { reapply };
}

export default useElementLayout;
